import { FormEvent, useEffect, useRef, useState } from "react";
import { Contact } from "../../../types/TradeFlow";
import { CrossIcon } from "../../Icons";
import { useTradeFlowTracker } from "../../../context/TradeFlowTrackerContext";
import { useTradeFlowData } from "../../../context/TradeFlowDataContext ";

const initialContact = {
  name: "",
  email: "",
  phone: "",
  role: "",
};

export default function Contacts() {
  const { isViewer } = useTradeFlowTracker();
  const { tradeFlowData, updateTradeFlow } = useTradeFlowData();
  const { trade_flow_json, id, row_id } = tradeFlowData;

  const handleAddContact = async (index: number, contact: Contact) => {
    if (isViewer) return;

    const updatedSteps = trade_flow_json.map((step, i) =>
      i === index
        ? {
            ...step,
            Contacts: [...step.Contacts, contact],
          }
        : step
    );

    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: updatedSteps,
    });
  };

  const handleSaveContact = async (
    stepIndex: number,
    contactIndex: number,
    updatedContact: { name: string; email: string; phone: string; role: string }
  ) => {
    const updatedSteps = trade_flow_json.map((step, i) => {
      if (i === stepIndex) {
        const updatedContacts = step.Contacts.map((contact, j) =>
          j === contactIndex ? updatedContact : contact
        );

        return {
          ...step,
          Contacts: updatedContacts,
        };
      }
      return step;
    });

    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: updatedSteps,
    });
  };

  const handleRemoveContact = async (index: number, contactIndex: number) => {
    const updatedSteps = trade_flow_json.map((step, i) =>
      i === index
        ? {
            ...step,
            Contacts: step.Contacts.filter((_, j) => j !== contactIndex),
          }
        : step
    );

    await updateTradeFlow({
      id,
      row_id,
      trade_flow_json: updatedSteps,
    });
  };

  return (
    <section className="w-full flex justify-start items-start gap-3 text-xs h-full overflow-auto will-change-transform">
      {trade_flow_json.map((el, idx) => (
        <div
          key={el.step_id}
          className="relative min-w-[200px] max-w-[150px] flex-shrink-0"
        >
          <div className="w-full h-[100px] flex flex-col items-center justify-start text-center text-gray-900 gap-2">
            <h4 className="size-8 text-lg bg-white z-10 flex items-center justify-center p-1 border-2 border-gray-900 rounded-full">
              {idx + 1}
            </h4>
            <p className="font-medium">{el.step.field}</p>
          </div>

          {idx < trade_flow_json.length - 1 && (
            <div className="absolute top-4 right-[-100px] w-full h-[1px] bg-gray-900" />
          )}
          {idx !== 0 && (
            <div className="absolute top-4 left-[-100px] w-full h-[1px] bg-gray-900" />
          )}

          <div className="space-y-2">
            {el.Contacts.map((contact, i) => (
              <div
                key={contact.contact_id || i}
                className="relative p-2 text-gray-500 space-y-2 border shadow-sm rounded-xl"
              >
                {!isViewer && (
                  <button
                    type="button"
                    className="absolute -top-[2px] -right-[4px] p-[1px] bg-gray-300 rounded-full hover:bg-red-500 hover:text-white hover:scale-90"
                    onClick={() => handleRemoveContact(idx, i)}
                  >
                    <CrossIcon className="size-4" />
                  </button>
                )}
                <EditableContactForm
                  stepIndex={idx}
                  contactIndex={i}
                  contact={contact}
                  handleSaveContact={handleSaveContact}
                  isViewer={isViewer}
                />
              </div>
            ))}

            <button
              disabled={isViewer}
              title={isViewer ? "You don’t have permission to edit" : undefined}
              className="
                w-full px-2 py-1 font-medium rounded-full border border-dashed
                bg-gray-100 text-gray-700
                disabled:bg-gray-200
                disabled:text-gray-400
                disabled:cursor-not-allowed
              "
              onClick={() => handleAddContact(idx, initialContact)}
            >
              + Add Contact
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

type Props = {
  stepIndex: number;
  contactIndex: number;
  contact: Contact;
  handleSaveContact: (
    stepIndex: number,
    contactIndex: number,
    updatedContact: Contact
  ) => void;
  isViewer: boolean;
};

type FieldKey = "name" | "email" | "phone" | "role";

function EditableContactForm({
  contact,
  handleSaveContact,
  stepIndex,
  contactIndex,
  isViewer,
}: Props) {
  const [isEditable, setIsEditable] = useState(false);
  const [focusField, setFocusField] = useState<FieldKey | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isEditable && focusField && formRef.current) {
      const input = formRef.current.querySelector<HTMLInputElement>(
        `input[name="${focusField}"]`
      );

      input?.focus();
    }
  }, [isEditable, focusField]);

  const handleEdit = (field: FieldKey) => {
    if (isViewer) return;

    setFocusField(field);
    setIsEditable(true);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const raw = Object.fromEntries(formData.entries());

    const values: Contact = {
      name: raw.name as string,
      email: raw.email as string,
      phone: raw.phone as string,
      role: raw.role as string,
    };

    handleSaveContact(stepIndex, contactIndex, values);

    setIsEditable(false);
  };

  return isEditable ? (
    <form className="w-full space-y-2" ref={formRef} onSubmit={handleSubmit}>
      <input
        className="w-full outline-none"
        type="text"
        name="name"
        placeholder="Enter name"
        defaultValue={contact.name}
        required
        minLength={2}
      />
      <input
        className="w-full outline-none"
        type="email"
        name="email"
        placeholder="Enter email"
        defaultValue={contact.email}
        required
      />
      <input
        className="w-full outline-none"
        type="tel"
        name="phone"
        placeholder="Enter phone"
        defaultValue={contact.phone}
      />
      <input
        className="w-full outline-none"
        type="text"
        name="role"
        placeholder="Enter role"
        defaultValue={contact.role}
      />
      <button
        className="w-full py-1 bg-custom-blue-medium text-white rounded-xl"
        id="save-contact-btn"
      >
        save
      </button>
    </form>
  ) : (
    <div className="space-y-2 overflow-hidden">
      <p
        onClick={() => handleEdit("name")}
        className={`${contact.name ? "font-medium text-gray-800" : ""}`}
      >
        {contact.name || "Enter name"}
      </p>
      <p onClick={() => handleEdit("email")}>
        {contact.email || "Enter email"}{" "}
      </p>
      <p onClick={() => handleEdit("phone")}>
        {contact.phone || "Enter phone"}
      </p>
      <p onClick={() => handleEdit("role")}>{contact.role || "Enter role"}</p>
    </div>
  );
}
